"use client"
import { useContext, useEffect, useRef, useState } from 'react'
import { ChooseYourPathDiagram } from './ChooseYourPathDiagram'
import { MOBILE_DIAGRAM_CONFIG } from './chooseYourPathDiagramModel'
import { CHOOSE_PATH_DESKTOP_CONFIGS, CHOOSE_PATH_MOBILE_CONFIGS, ChoosePathDesignContext } from './choosePathDesigns'

// The desktop config needs roughly this much CONTAINER width for its 18px
// labels to fit the 75px row gaps. Below it the mobile config takes over.
// Deliberately measured on the container, NOT a viewport breakpoint: the
// text column's width is not proportional to the window's (the fixed HUD
// squeezes it to ~385px between 900-1024, and it's still only ~450-600px
// up to ~1200), so viewport breakpoints kept picking the wrong config.
// Must match the 640px in the [@container(min-width:640px)] classes below,
// which make the same cut in CSS before hydration.
const CHOOSE_PATH_DESKTOP_MIN_WIDTH_PX = 640

export const ChooseYourPathOG = ({
  hoveredPlanIndex = null,
}: {
  /** Plan-box index under the pointer (from the choices grid). */
  hoveredPlanIndex?: number | null
}) => {
  // Per-tab design (see CHOOSE_PATH_DIAGRAM_DESIGNS in hud/variants); designs
  // without a mobile entry fall back to the stock mobile config when narrow.
  const design = useContext(ChoosePathDesignContext)
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setWidth(el.getBoundingClientRect().width)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const desktopConfig = CHOOSE_PATH_DESKTOP_CONFIGS[design]
  const mobileConfig = CHOOSE_PATH_MOBILE_CONFIGS[design] ?? MOBILE_DIAGRAM_CONFIG
  // Until the JS measurement lands (width == null — the whole SSR-to-hydration
  // window, which on a cold load is seconds long), BOTH configs render and a
  // CSS container query on the wrapper picks the one to show, making the same
  // >=640px container-width cut the JS below makes. So the server HTML already
  // carries the right diagram at its estimated-row-height size — the reader
  // never sees the flowchart region collapsed to zero height with the plan
  // buttons pulled up over the heading — and hydration only fine-tunes it
  // (measured row heights, pixel-snapped boxes, arrows). Once measured, the
  // matching wrapper stays mounted (keyed) and just drops its hiding classes;
  // the other unmounts.
  const showDesktop = width == null || width >= CHOOSE_PATH_DESKTOP_MIN_WIDTH_PX
  const showMobile = width == null || width < CHOOSE_PATH_DESKTOP_MIN_WIDTH_PX
  return (
    <div className="-mt-40">
      <div ref={containerRef} className="mt-20 [container-type:inline-size]">
        {showDesktop && (
          <div
            key="desktop"
            className={width == null ? 'hidden [@container(min-width:640px)]:block' : undefined}
          >
            <ChooseYourPathDiagram config={desktopConfig} hoveredPlanIndex={hoveredPlanIndex} />
          </div>
        )}
        {showMobile && (
          <div
            key="mobile"
            className={width == null ? '[@container(min-width:640px)]:hidden' : undefined}
          >
            <ChooseYourPathDiagram config={mobileConfig} hoveredPlanIndex={hoveredPlanIndex} />
          </div>
        )}
      </div>
    </div>
  )
}
